import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Selection } from '../../components/PreviousSelector';
import { placeResultToAddress } from '../../components/SearchLocationInput';

type PlaceResult = google.maps.places.PlaceResult;

export interface Appointment {
  isBooked?: boolean;
  date: string;
  routeId: string;
  /** Percent added to the services subtotal for this slot (negative = off). */
  priceAdjustmentPercent?: number | null;
  /** Fixed cents added on top of the percentage. */
  priceAdjustmentCents?: number | null;
  /** DISCOUNT, STANDARD or PEAK. */
  priceTier?: string | null;
  /** True for the cheapest slots in the loaded range. */
  isLowestPrice?: boolean | null;
  /**
   * The start as an instant with the service area's offset (server
   * Availability.startsAt). `date` is the same start as a wall time in
   * `timeZone`. Book with util/slotTime slotInstant().
   */
  startsAt?: string | null;
  /** The service area's IANA zone that `date` is a wall time in. */
  timeZone?: string | null;
  /**
   * The name of the lead-time rule that priced this slot ("Book 14+ days
   * ahead"), null when none applied; the names of every rule that did.
   * Absent from a server that does not send them yet.
   */
  priceAdjustmentLabel?: string | null;
  appliedRules?: string[] | null;
}

export interface Address {
  street: string;
  city: string;
  postalCode: string;
  country: string;
  province: string;
  countryCode: string;
  provinceCode: string;
  lat?: number;
  lng?: number;
}

export interface Contact {
  name: string;
  email: string;
  phoneNumber: string;
}

export interface ServiceLocationState {
  serviceAddress?: Address;
  /**
   * The service-area answer (getPostal) for `serviceAddress`: true, false,
   * or undefined while unknown (not asked yet, or the check failed - which
   * never blocks anyone). Only a known `false` closes the later steps and
   * the prepay option; it is forgotten as soon as the address changes.
   */
  inService?: boolean;
  newServiceAddress?: PlaceResult;
  selectedAddress: Selection;
  dateTime?: Appointment;
  contact: Contact;
  selectedContact: Selection;
}

/** Same place: a new address object for it keeps its coverage answer. */
const sameAddress = (a?: Address, b?: Address): boolean => (
  !!a && !!b
  && a.street === b.street
  && a.postalCode === b.postalCode
  && a.city === b.city
  && (a.lat ?? null) === (b.lat ?? null)
  && (a.lng ?? null) === (b.lng ?? null)
);

const initialState: ServiceLocationState = {
  contact: {
    name: '',
    email: '',
    phoneNumber: '',
  },
  selectedAddress: 'unset',
  selectedContact: 'unset',
};

export const serviceLocationSlice = createSlice({
  name: 'seviceLocation',
  initialState,
  reducers: {
    setNewServiceAddress: (
      state,
      action: PayloadAction<PlaceResult | undefined>,
    ) => {
      const address = placeResultToAddress(action.payload);
      return {
        ...state,
        newServiceAddress: action.payload,
        serviceAddress: address,
        inService: sameAddress(address, state.serviceAddress)
          ? state.inService
          : undefined,
        selectedAddress: 'new',
      };
    },
    setServiceAddress: (
      state,
      action: PayloadAction<[Selection, Address | undefined]>,
    ) => {
      const [selection, address] = action.payload;
      return {
        ...state,
        serviceAddress: address,
        inService: sameAddress(address, state.serviceAddress)
          ? state.inService
          : undefined,
        selectedAddress: selection,
      };
    },
    /** Records the service-area answer for the current address. */
    setServiceCoverage: (
      state,
      action: PayloadAction<boolean | undefined>,
    ) => ({
      ...state,
      inService: action.payload,
    }),
    setDateTime: (
      state,
      action: PayloadAction<Appointment>,
    ) => ({
      ...state,
      dateTime: action.payload,
      isBooked: action.payload,
    }),
    setName: (state, action: PayloadAction<string>) => ({
      ...state,
      contact: {
        ...state.contact,
        name: action.payload,
      },
      selectedContact: 'new',
    }),
    setEmail: (state, action: PayloadAction<string>) => ({
      ...state,
      contact: {
        ...state.contact,
        email: action.payload,
      },
      selectedContact: 'new',
    }),
    setPhone: (state, action: PayloadAction<string>) => ({
      ...state,
      contact: {
        ...state.contact,
        phoneNumber: action.payload,
      },
      selectedContact: 'new',
    }),
    setNewContact: (state) => ({
      ...state,
      contact: {
        name: '',
        email: '',
        phoneNumber: '',
      },
      selectedContact: 'new',
    }),
    setContact: (
      state,
      {
        payload: [selectedContact, contact],
      }: PayloadAction<[Selection, Contact]>,
    ) => ({
      ...state,
      selectedContact,
      contact,
    }),
    clearContact: (state) => (
      {
        ...state,
        contact: {
          name: '',
          email: '',
          phoneNumber: '',
        },
        selectedContact: 'unset',
      }
    ),
  },
});

export const {
  setServiceAddress,
  setServiceCoverage,
  setDateTime,
  setName,
  setEmail,
  setPhone,
  setContact,
  setNewContact,
  setNewServiceAddress,
  clearContact,
} = serviceLocationSlice.actions;

export default serviceLocationSlice.reducer;
